import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getOptimizedImageUrl(url: string, width: number = 800, height?: number) {
  if (!url) return url
  try {
      const safeWidth = Math.max(1, Math.round(width))

      // Public Supabase URLs: /storage/v1/object/public/<bucket>/<path>
      // → /storage/v1/render/image/public/<bucket>/<path>?width=…
      if (url.includes('/storage/v1/object/public/')) {
                const optimizedBase = url.replace('/object/public/', '/render/image/public/')
                const params = new URLSearchParams({
                  width: String(safeWidth),
                  quality: '80'
                })
                if (typeof height === 'number' && Number.isFinite(height) && height > 0) {
                  params.set('height', String(Math.max(1, Math.round(height))))
                  params.set('resize', 'contain')
                }
                return `${optimizedBase}?${params.toString()}`
            }

      // Signed URLs for the private bucket:
      //   /storage/v1/object/sign/<bucket>/<path>?token=…
      // Supabase exposes the same image-transformation endpoint at
      //   /storage/v1/render/image/sign/<bucket>/<path>?token=…&width=…
      // The signed token stays valid for the render path too.
      if (url.includes('/storage/v1/object/sign/')) {
            const [base, query = ''] = url.split('?')
            const optimizedBase = base.replace('/object/sign/', '/render/image/sign/')
            const params = new URLSearchParams(query)
            params.set('width', String(safeWidth))
            params.set('quality', '80')
            if (typeof height === 'number' && Number.isFinite(height) && height > 0) {
                params.set('height', String(Math.max(1, Math.round(height))))
                params.set('resize', 'contain')
            }
            return `${optimizedBase}?${params.toString()}`
      }

      return url
  } catch (e) {
      return url
  }
}

export function parseAspectRatio(value?: string | number): number {
  if (!value) return 1;
  if (typeof value === 'number') return value;

  if (typeof value === 'string') {
    // "W:H" aspect ratio form
    if (value.includes(':')) {
      const [w, h] = value.split(':').map(Number);
      if (w && h) return w / h;
    }
    // "WxH" resolution form — common when the user picks a concrete size
    if (/x/i.test(value)) {
      const [w, h] = value.split(/x/i).map(Number);
      if (w && h) return w / h;
    }
  }

  return 1;
}

/**
 * Extract an aspect ratio from a generation's params in a UI-friendly way.
 * Falls through aspect_ratio → image_dimensions → aspectRatio (camelCase defensive)
 * → resolution / quality (WxH) → explicit width/height. Returns null when nothing
 * usable is present so callers can decide between a default and a flat skeleton.
 */
export function resolveGenerationRatio(params?: Record<string, unknown> | null): number | null {
  if (!params) return null;
  const candidates = [
    params.aspect_ratio,
    params.image_dimensions,
    (params as Record<string, unknown>).aspectRatio,
    params.ratio,
    params.resolution,
    params.quality,
  ];
  for (const raw of candidates) {
    if (typeof raw !== 'string') continue;
    if (!raw || raw === 'auto') continue;
    const parsed = parseAspectRatio(raw);
    if (parsed && parsed !== 1) return parsed;
    // "1:1" / "1024x1024" parse to 1 legitimately — accept once we saw a real value
    if (parsed === 1 && (raw.includes(':') || /x/i.test(raw))) return 1;
  }
  const w = Number(params.width);
  const h = Number(params.height);
  if (w > 0 && h > 0) return w / h;
  return null;
}

export function getPreviewBoxSize(ratio: number) {
  // Base size for the short side
  const BASE_SIZE = 76;
  // Max size for the long side to prevent huge items
  const MAX_LONG_SIDE = 124;

  let width: number;
  let height: number;

  if (ratio >= 1) {
    // Horizontal or Square
    height = BASE_SIZE;
    width = Math.min(Math.max(BASE_SIZE * ratio, BASE_SIZE), MAX_LONG_SIDE);
  } else {
    // Vertical
    width = BASE_SIZE;
    height = Math.min(Math.max(BASE_SIZE / ratio, BASE_SIZE), MAX_LONG_SIDE);
  }

  return { width, height };
}
